import { Injectable, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';

import { BehaviorSubject, combineLatest, Observable, Subscription, tap } from 'rxjs';

import { savedSourceDateTypeOptions } from '../../../Common/Forms/components/DateRangeSelector/date-range-selector.component';
import { DropdownOptionModel } from '../../../Common/Forms/models/dropdown-option.model';
import { MonitorConfigService } from '../../../Common/MonitorShared/services/monitor-config.service';
import { SavedFilterService } from '../../../Common/Filter/services/saved-filter.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';

import { FolderService } from '@Monitor/Core/services/folder.service';
import { getMonitoringDateQueryInputFolderOptions, getMonitoringDateQueryInputTopicOptions } from '../models/monitoring-date-query-input-options';
import { MentionSearchModel } from '../models/mention-search.model';
import { MonitoringRoutingService } from '../../Core/services/monitoring-routing.service';
import { RouteType } from '../enums/route-type.enum';
import { TopicsService } from '../../Core/services/topics.service';
import { MonitoringRouteModel } from '../models/monitoring-route.model';
import { MentionSearchService } from '@Monitor/Core/services/mention-search.service';
import { MediaSubscriptionService } from '@Monitor/Core/services/media-subscription.service';
import { LocalizationService } from '@Core/Localization/services/localization.service';


/**
 * This service provides functionalities on monitoring analytics dashboard that are unrelated to searching
 * For searching logic, go to MonitoringAnalyticsDashboardSearchService
 */
@Injectable()
export class MonitoringAnalyticsDashboardService implements OnDestroy {

    currentRoute: MonitoringRouteModel;
    mentionSearch: MentionSearchModel;

    subscriptions: Subscription[] = [];
    protected pageHeading$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    protected pageSRHeading$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    protected dateFilterOptions$: BehaviorSubject<DropdownOptionModel[]> = new BehaviorSubject<DropdownOptionModel[]>([]);
    protected dateTypeFilterOptions$: BehaviorSubject<DropdownOptionModel[]> = new BehaviorSubject<DropdownOptionModel[]>([]);

    constructor(
        protected currentUserStateService: CurrentUserStateService,
        protected monitorConfigService: MonitorConfigService,
        protected savedFilterService: SavedFilterService,
        protected topicsService: TopicsService,
        protected routingService: MonitoringRoutingService,
        protected folderService: FolderService,
        protected mentionSearchService: MentionSearchService,
        protected mediaSubscriptionService: MediaSubscriptionService,
        protected titleService: Title,
        protected localizationService: LocalizationService

    ) {
        this.subscriptions.push(
            combineLatest([
                this.routingService.getCurrentRoute().pipe(
                    tap(route => {
                        this.currentRoute = route;
                        if (route?.type === RouteType.allSavedCoverageAnalyticsDashboard) {
                            const savedCoverageHeading = this.localizationService.transform('Monitoring.result.savedCoverage.heading');
                            this.pageHeading$.next(savedCoverageHeading);
                            this.pageSRHeading$.next('Monitor.primaryHeading.folder');
                        }
                    })
                ),
                this.topicsService.getFocusedTopic().pipe(
                    tap(topic => {
                        this.mentionSearch = topic;
                        if (topic) {
                            this.titleService.setTitle(
                                this.localizationService.transform('Monitoring.topics.browserTitle.name', {name: topic.name})
                            );
                            this.pageHeading$.next(topic.name);
                            this.pageSRHeading$.next('Monitor.primaryHeading.topic');
                        }
                    })
                ),
                this.folderService.currentResource$.pipe(
                    tap(folder => {
                        if (folder) {
                            this.titleService.setTitle(
                                this.localizationService.transform('Monitoring.savedCoverage.browserTitle.name', {name: folder.name})
                            );
                            this.pageHeading$.next(folder.name);
                            this.pageSRHeading$.next('Monitor.primaryHeading.folder');
                        }
                    })
                ),
                this.mediaSubscriptionService.currentResource$.pipe(
                    tap(subscription => {
                        if (subscription) {
                            this.titleService.setTitle(
                                this.localizationService.transform('Monitoring.subscriptions.browserTitle.name', {name: subscription.name})
                            );
                            this.pageHeading$.next(subscription.name);
                            this.pageSRHeading$.next('Monitor.primaryHeading.subscription');
                        }
                    })
                )
            ]).subscribe(([route, search]) => {
                this.setActiveDateFilterOptions();
            }),

        );
    }

    isUnsavedMentionRoute(): boolean {
        const unsavedMentionRoute = [
            RouteType.topicAnalyticsDashboard,
            RouteType.subscriptionAnalyticsDashboard,
            RouteType.topicAnalyticsSyndicates,
            RouteType.subscriptionAnalyticsSyndicates
        ];
        return unsavedMentionRoute.includes(this.currentRoute.type);
    }

    setActiveDateFilterOptions() {
        if (!this.currentRoute) {
            return;
        }
        switch (this.currentRoute.type) {
            case RouteType.allSavedCoverageAnalyticsDashboard:
            case RouteType.folderAnalyticsDashboard:
            case RouteType.allSavedCoverageAnalyticsSyndicates:
            case RouteType.folderAnalyticsSyndicates:
                this.dateFilterOptions$.next(getMonitoringDateQueryInputFolderOptions());
                this.dateTypeFilterOptions$.next(savedSourceDateTypeOptions);
                break;
            case RouteType.adHocAnalyticsDashboard:
            case RouteType.adHocAnalyticsSyndicates:
                if (this.mentionSearch && this.mentionSearch.searchCriteria && this.mentionSearch.searchCriteria.isAccountSearch) {
                    this.dateFilterOptions$.next(getMonitoringDateQueryInputFolderOptions());
                    this.dateTypeFilterOptions$.next(savedSourceDateTypeOptions);
                } else {
                    this.dateFilterOptions$.next(getMonitoringDateQueryInputTopicOptions());
                }
                break;
            default:
                this.dateFilterOptions$.next(getMonitoringDateQueryInputTopicOptions());
                break;
        }
    }

    //#region getters

    getPageHeading(): Observable<string> {
        return this.pageHeading$;
    }

    getPageSRHeading(): Observable<string> {
        return this.pageSRHeading$;
    }

    getDateFilterOptions(): Observable<DropdownOptionModel[]> {
        return this.dateFilterOptions$;
    }

    getDateTypeFilterOptions(): Observable<DropdownOptionModel[]> {
        return this.dateTypeFilterOptions$;
    }

    //#endregion

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
